import { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Loader2, Info, Users, Baby, Calendar as CalendarIcon } from "lucide-react";

const MONTH_NAMES = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  it: ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
  es: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
  fr: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  pt: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
  ru: ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
  "zh-Hans": ["一月", "二月", "三月", "四月", "五月", "六月", "七月", "八月", "九月", "十月", "十一月", "十二月"],
  ko: ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"],
  ja: ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"],
  ar: ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
  hi: ["जनवरी", "फरवरी", "मार्च", "अप्रैल", "मई", "जून", "जुलाई", "अगस्त", "सितंबर", "अक्टूबर", "नवंबर", "दिसंबर"]
};

const WEEKDAYS = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  it: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
  es: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
  fr: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
  de: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  pt: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  ru: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
  "zh-Hans": ["日", "一", "二", "三", "四", "五", "六"],
  ko: ["일", "월", "화", "수", "목", "금", "토"],
  ja: ["日", "月", "火", "水", "木", "金", "土"],
  ar: ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"],
  hi: ["रवि", "सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि"]
};

const TRANSLATIONS = {
  selectDates: {
    en: "Select your dates", it: "Seleziona le date", es: "Selecciona fechas", fr: "Choisissez vos dates",
    de: "Wählen Sie Ihre Daten", pt: "Selecione suas datas", ru: "Выберите даты", "zh-Hans": "选择日期", ko: "날짜 선택", ja: "日付を選択", ar: "اختر التواريخ", hi: "तारीखें चुनें"
  },
  checkIn: {
    en: "Check-in", it: "Check-in", es: "Entrada", fr: "Arrivée", de: "Anreise", pt: "Check-in", ru: "Заезд", "zh-Hans": "入住", ko: "체크인", ja: "チェックイン", ar: "الوصول", hi: "चेक-इन"
  },
  checkOut: {
    en: "Check-out", it: "Check-out", es: "Salida", fr: "Départ", de: "Abreise", pt: "Check-out", ru: "Выезд", "zh-Hans": "退房", ko: "체크아웃", ja: "チェックアウト", ar: "المغادرة", hi: "चेक-आउट"
  },
  nights: {
    en: "nights", it: "notti", es: "noches", fr: "nuits", de: "Nächte", pt: "noites", ru: "ночей", "zh-Hans": "晚", ko: "박", ja: "泊", ar: "ليالي", hi: "रातें"
  },
  adults: {
    en: "Adults", it: "Adulti", es: "Adultos", fr: "Adultes", de: "Erwachsene", pt: "Adultos", ru: "Взрослые", "zh-Hans": "成人", ko: "성인", ja: "大人", ar: "بالغون", hi: "वयस्क"
  },
  children: {
    en: "Children (+1Y)", it: "Bambini (+1Y)", es: "Niños (+1Y)", fr: "Enfants (+1Y)", de: "Kinder (+1Y)", pt: "Crianças (+1Y)", ru: "Дети (+1Y)", "zh-Hans": "儿童 (+1Y)", ko: "어린이 (+1Y)", ja: "子供 (+1Y)", ar: "أطفال (+1Y)", hi: "बच्चे (+1Y)"
  },
  infants: {
    en: "Infants (0Y)", it: "Neonati (0Y)", es: "Bebés (0Y)", fr: "Bébés (0Y)", de: "Babys (0Y)", pt: "Bebês (0Y)", ru: "Младенцы (0Y)", "zh-Hans": "婴儿 (0Y)", ko: "유아 (0Y)", ja: "乳児 (0Y)", ar: "رضع (0Y)", hi: "शिशु (0Y)"
  },
  infantsFull: {
    en: "Infants (0Y)", it: "Neonati (0Y)", es: "Bebés (0Y)", fr: "Bébés (0Y)", de: "Babys (0Y)", pt: "Bebês (0Y)", ru: "Младенцы (0Y)", "zh-Hans": "婴儿 (0Y)", ko: "유아 (0Y)", ja: "乳児 (0Y)", ar: "رضع (0Y)", hi: "शिशु (0Y)"
  },
  childrenFull: {
    en: "Children (+1Y)", it: "Bambini (+1Y)", es: "Niños (+1Y)", fr: "Enfants (+1Y)", de: "Kinder (+1Y)", pt: "Crianças (+1Y)", ru: "Дети (+1Y)", "zh-Hans": "儿童 (+1Y)", ko: "어린이 (+1Y)", ja: "子供 (+1Y)", ar: "أطفال (+1Y)", hi: "बच्चे (+1Y)"
  },
  minNights: {
    en: "Minimum stay", it: "Soggiorno minimo", es: "Estancia mínima", fr: "Séjour minimum", de: "Mindestaufenthalt", pt: "Estadia mínima", ru: "Минимум ночей", "zh-Hans": "最少入住", ko: "최소 숙박", ja: "最低宿泊数", ar: "الحد الأدنى للإقامة", hi: "न्यूनतम प्रवास"
  },
  maxGuests: {
    en: "Maximum guests", it: "Ospiti massimi", es: "Huéspedes máx.", fr: "Capacité max.", de: "Max. Gäste", pt: "Máx. hóspedes", ru: "Макс. гостей", "zh-Hans": "最多人数", ko: "최대 인원", ja: "最大人数", ar: "الحد الأقصى للضيوف", hi: "अधिकतम मेहमान"
  },
  maxGuestsReached: {
    en: "Maximum guest capacity reached.", it: "Hai raggiunto il numero massimo di ospiti.", es: "Capacidad máxima alcanzada.", fr: "Capacité maximale atteinte.", de: "Maximale Gästeanzahl erreicht.", pt: "Capacidade máxima atingida.", ru: "Достигнуто максимальное количество гостей.", "zh-Hans": "已达最大人数。", ko: "최대 인원에 도달했습니다.", ja: "最大人数に達しました。", ar: "تم الوصول للحد الأقصى من الضيوف.", hi: "अधिकतम मेहमान क्षमता पूरी हो गई।"
  },
  available: {
    en: "Available", it: "Disponibile", es: "Disponible", fr: "Disponible", de: "Verfügbar", pt: "Disponível", ru: "Доступно", "zh-Hans": "可预订", ko: "예약 가능", ja: "空室あり", ar: "متاح", hi: "उपलब्ध"
  },
  unavailable: {
    en: "Unavailable", it: "Non disponibile", es: "No disponible", fr: "Indisponible", de: "Nicht verfügbar", pt: "Indisponível", ru: "Недоступно", "zh-Hans": "不可预订", ko: "예약 불가", ja: "空室なし", ar: "غير متاح", hi: "अनुपलब्ध"
  },
  pricePerNight: {
    en: "per night", it: "a notte", es: "por noche", fr: "par nuit", de: "pro Nacht", pt: "por noite", ru: "за ночь", "zh-Hans": "每晚", ko: "1박", ja: "1泊", ar: "لليلة", hi: "प्रति रात"
  },
  priceFrom: {
    en: "From", it: "Da", es: "Desde", fr: "À partir de", de: "Ab", pt: "A partir de", ru: "От", "zh-Hans": "从", ko: "부터", ja: "から", ar: "من", hi: "से"
  },
  noPriceSet: {
    en: "Price on request", it: "Prezzo su richiesta", es: "Precio bajo petición", fr: "Prix sur demande", de: "Preis auf Anfrage", pt: "Preço sob consulta", ru: "Цена по запросу", "zh-Hans": "价格待询", ko: "문의 요망", ja: "価格はお問い合わせください", ar: "السعر عند الطلب", hi: "अनुरोध पर कीमत"
  },
  clearDates: {
    en: "Clear dates", it: "Cancella date", es: "Borrar fechas", fr: "Effacer dates", de: "Daten löschen", pt: "Limpar datas", ru: "Очистить даты", "zh-Hans": "清除日期", ko: "날짜 지우기", ja: "日付をクリア", ar: "مسح التواريخ", hi: "तारीखें साफ करें"
  },
  loading: {
    en: "Loading...", it: "Caricamento...", es: "Cargando...", fr: "Chargement...", de: "Laden...", pt: "Carregando...", ru: "Загрузка...", "zh-Hans": "加载中...", ko: "로딩 중...", ja: "読み込み中...", ar: "جاري التحميل...", hi: "लोड हो रहा है..."
  },
  childAge: {
    en: "Child", it: "Bambino", es: "Niño", fr: "Enfant", de: "Kind", pt: "Criança", ru: "Ребёнок", "zh-Hans": "儿童", ko: "어린이", ja: "子供", ar: "طفل", hi: "बच्चा"
  },
  ageYears: {
    en: "age", it: "età", es: "edad", fr: "âge", de: "Alter", pt: "idade", ru: "лет", "zh-Hans": "岁", ko: "세", ja: "歳", ar: "سنة", hi: "उम्र"
  },
  selectAge: {
    en: "Select age", it: "Seleziona età", es: "Selecciona edad", fr: "Choisir l'âge", de: "Alter wählen", pt: "Selecionar idade", ru: "Выберите возраст", "zh-Hans": "选择年龄", ko: "나이 선택", ja: "年齢を選択", ar: "اختر العمر", hi: "उम्र चुनें"
  }
};

function formatDate(dateStr, lang = "en") {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDate();
  const month = MONTH_NAMES[lang]?.[d.getMonth()] || MONTH_NAMES.en[d.getMonth()];
  return `${day} ${month}`;
}

export default function BookingCalendar({ 
  language = "en", 
  onDateSelect, 
  onGuestsChange,
  calendarData = [],
  config = {},
  loading = false
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [childrenAges, setChildrenAges] = useState([]);

  const t = useCallback((key) => TRANSLATIONS[key]?.[language] || TRANSLATIONS[key]?.en || key, [language]);
  const monthNames = MONTH_NAMES[language] || MONTH_NAMES.en;
  const weekdays = WEEKDAYS[language] || WEEKDAYS.en;

  // Create a map for quick lookup
  const calendarMap = useMemo(() => {
    const map = {};
    calendarData.forEach(d => { map[d.date] = d; });
    return map;
  }, [calendarData]);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];

    // Padding days from previous month
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, isPadding: true });
    }

    // Days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayData = calendarMap[dateStr] || { date: dateStr, available: true, price: null };
      const isPast = new Date(dateStr) < new Date(new Date().toDateString());
      days.push({ ...dayData, isPast });
    }

    return days;
  }, [currentMonth, calendarMap]);

  // Calculate date limits (current month to 12 months ahead)
  const minMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);
  
  const maxMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 11, 1); // 12 months from now
  }, []);
  
  // Check if navigation is allowed
  const canGoPrev = currentMonth > minMonth;
  const canGoNext = currentMonth < maxMonth;
  
  // Navigate months with limits
  const prevMonth = () => {
    if (canGoPrev) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    }
  };
  const nextMonth = () => {
    if (canGoNext) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    }
  };

  // Handle date selection
  const handleDateClick = (day) => {
    if (day.isPadding || day.isPast) return;
    
    if (!checkIn || (checkIn && checkOut)) {
      // Start new selection - CHECK-IN must be available
      if (!day.available) return;
      setCheckIn(day.date);
      setCheckOut(null);
    } else {
      // Complete selection - CHECK-OUT can be on a blocked day
      // (the blocked day is when guest leaves, not a night they stay)
      if (day.date > checkIn) {
        // Check if all NIGHTS in range are available (excludes check-out day)
        const start = new Date(checkIn);
        const end = new Date(day.date);
        let allNightsAvailable = true;
        let unavailableDate = null;
        let current = new Date(start);
        
        while (current < end) {  // < not <= because check-out is not a night
          const dateStr = current.toISOString().split("T")[0];
          const dayInfo = calendarMap[dateStr];
          if (dayInfo && !dayInfo.available) {
            allNightsAvailable = false;
            unavailableDate = dateStr;
            break;
          }
          current.setDate(current.getDate() + 1);
        }
        
        if (allNightsAvailable) {
          setCheckOut(day.date);
        } else {
          // Reset and start new selection
          setCheckIn(day.date);
          setCheckOut(null);
        }
      } else {
        // Clicked date before check-in, reset
        setCheckIn(day.date);
        setCheckOut(null);
      }
    }
  };

  // Clear dates
  const clearDates = () => {
    setCheckIn(null);
    setCheckOut(null);
    setHoverDate(null);
  };

  // Notify parent of changes and scroll on mobile
  useEffect(() => {
    if (onDateSelect) {
      onDateSelect({ checkIn, checkOut });
    }
    
    // Auto-scroll to summary on mobile when both dates are selected
    if (checkIn && checkOut && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setTimeout(() => {
        const summaryEl = document.getElementById('booking-summary');
        if (summaryEl) {
          summaryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [checkIn, checkOut, onDateSelect]);

  // Sync children ages array when children count changes
  useEffect(() => {
    setChildrenAges(prev => {
      if (prev.length === children) return prev;
      if (prev.length < children) {
        // Add empty slots for new children
        return [...prev, ...Array(children - prev.length).fill("")];
      }
      // Remove extra slots
      return prev.slice(0, children);
    });
  }, [children]);

  useEffect(() => {
    if (onGuestsChange) {
      onGuestsChange({ adults, children, infants, childrenAges });
    }
  }, [adults, children, infants, childrenAges, onGuestsChange]);

  // Handle child age change
  const handleChildAgeChange = (index, value) => {
    setChildrenAges(prev => {
      const newAges = [...prev];
      newAges[index] = value;
      return newAges;
    });
  };

  // Check if date is in selection range
  const isInRange = (dateStr) => {
    if (!checkIn) return false;
    const endDate = checkOut || hoverDate;
    if (!endDate) return dateStr === checkIn;
    return dateStr >= checkIn && dateStr <= endDate;
  };

  // Calculate nights
  const totalNights = checkIn && checkOut ? 
    Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) : 0;

  // Calcola il primo checkout OBBLIGATORIO (giorno bloccato)
  // REGOLA CORRETTA:
  // - Evidenziare SOLO il primo giorno BLOCCATO (è l'unico checkout forzato)
  // - NON evidenziare giorni disponibili (l'utente può scegliere liberamente)
  // - Il blocco deve essere raggiungibile (>= check-in + min_nights)
  const firstValidCheckout = useMemo(() => {
    if (!checkIn || checkOut) return null;
    
    const minNights = config?.min_nights || 1;
    const checkInDate = new Date(checkIn);
    
    // Calcola minCheckout
    const minCheckoutDate = new Date(checkInDate);
    minCheckoutDate.setDate(minCheckoutDate.getDate() + minNights);
    const minCheckoutStr = minCheckoutDate.toISOString().split("T")[0];
    
    // Cerca il primo giorno BLOCCATO dopo check-in
    let testDate = new Date(checkInDate);
    testDate.setDate(testDate.getDate() + 1); // Inizia dal giorno dopo check-in
    const maxLookAhead = 365;
    
    for (let i = 0; i < maxLookAhead; i++) {
      const testStr = testDate.toISOString().split("T")[0];
      const dayInfo = calendarMap[testStr];
      
      // Trovato un giorno bloccato
      if (dayInfo && !dayInfo.available) {
        // Verifica se è raggiungibile (>= minCheckout)
        if (testStr >= minCheckoutStr) {
          // È raggiungibile: evidenzia questo blocco come checkout forzato
          return testStr;
        } else {
          // Blocco troppo vicino: nessun checkout possibile
          return null;
        }
      }
      testDate.setDate(testDate.getDate() + 1);
    }
    
    // Nessun blocco trovato: l'utente può scegliere liberamente
    // NON evidenziare nulla
    return null;
  }, [checkIn, checkOut, config?.min_nights, calendarMap]);

  // Get min price from visible dates
  const minPrice = useMemo(() => {
    const prices = calendarDays.filter(d => d.price && d.available && !d.isPast).map(d => d.price);
    return prices.length > 0 ? Math.min(...prices) : null;
  }, [calendarDays]);

  // Use strict check - only fallback if undefined/null, not if 0
  const maxGuests = config?.max_guests != null ? config.max_guests : 6;
  const baseGuests = config?.base_guests != null ? config.base_guests : 2;
  const minNightsDefault = config?.min_nights != null ? config.min_nights : 2;
  
  // Calculate total guests including infants
  const totalGuests = adults + children + infants;
  const isAtMaxCapacity = totalGuests >= maxGuests;
  
  // Debug log
  console.log("BookingCalendar config:", { maxGuests, baseGuests, minNightsDefault, totalGuests, isAtMaxCapacity, rawConfig: config });

  return (
    <div className="bg-white rounded-lg shadow-lg border border-[#E7E5E4] overflow-hidden">
      {/* Header */}
      <div className="bg-[#1C1917] text-white p-6">
        <h3 className="font-heading text-xl mb-2">{t("selectDates")}</h3>
        {minPrice && (
          <p className="text-white/60 text-sm">
            {t("priceFrom")} <span className="text-[#D4AF37] font-semibold">€{minPrice}</span> {t("pricePerNight")}
          </p>
        )}
      </div>

      {/* Selected dates summary */}
      {(checkIn || checkOut) && (
        <div className="border-b border-[#E7E5E4] p-4 bg-[#FDF9EF]">
          <div className="flex items-center justify-between">
            <div className="flex gap-8">
              <div>
                <span className="text-xs text-[#78716C] uppercase tracking-wider">{t("checkIn")}</span>
                <p className="font-medium text-[#1C1917]">{checkIn ? formatDate(checkIn, language) : "—"}</p>
              </div>
              <div>
                <span className="text-xs text-[#78716C] uppercase tracking-wider">{t("checkOut")}</span>
                <p className="font-medium text-[#1C1917]">{checkOut ? formatDate(checkOut, language) : "—"}</p>
              </div>
              {totalNights > 0 && (
                <div>
                  <span className="text-xs text-[#78716C] uppercase tracking-wider">&nbsp;</span>
                  <p className="font-medium text-[#D4AF37]">{totalNights} {t("nights")}</p>
                </div>
              )}
            </div>
            <button onClick={clearDates} className="text-sm text-[#78716C] hover:text-[#1C1917] underline">
              {t("clearDates")}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 flex items-center justify-center">
          <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          <span className="ml-3 text-[#78716C]">{t("loading")}</span>
        </div>
      ) : (
        <>
          {/* Calendar navigation */}
          <div className="flex items-center justify-between p-4 border-b border-[#E7E5E4]">
            <button 
              onClick={prevMonth} 
              disabled={!canGoPrev}
              className={`p-2 rounded-full transition-colors ${canGoPrev ? 'hover:bg-[#F9F8F6]' : 'opacity-30 cursor-not-allowed'}`}
            >
              <ChevronLeft size={20} className="text-[#78716C]" />
            </button>
            <h4 className="font-heading text-lg">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h4>
            <button 
              onClick={nextMonth} 
              disabled={!canGoNext}
              className={`p-2 rounded-full transition-colors ${canGoNext ? 'hover:bg-[#F9F8F6]' : 'opacity-30 cursor-not-allowed'}`}
            >
              <ChevronRight size={20} className="text-[#78716C]" />
            </button>
          </div>

          {/* Calendar grid */}
          <div className="p-4">
            {/* Weekdays header */}
            <div className="grid grid-cols-7 mb-2">
              {weekdays.map((day, i) => (
                <div key={i} className="text-center text-xs text-[#78716C] uppercase tracking-wider py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, i) => {
                if (day.isPadding) {
                  return <div key={i} className="aspect-square" />;
                }

                const isSelected = day.date === checkIn || day.date === checkOut;
                const inRange = isInRange(day.date);
                const isCheckInDay = day.date === checkIn;
                const isCheckOutDay = day.date === checkOut;
                
                // NUOVA LOGICA: evidenzia SOLO il primo checkout valido
                const isFirstValidCheckoutDay = firstValidCheckout && day.date === firstValidCheckout && !checkOut;
                
                // Logica disabled:
                // - Passato: sempre disabled
                // - Non disponibile E non c'è checkIn: disabled (non può essere check-in)
                // - Con checkIn selezionato: solo il firstValidCheckout è cliccabile (se non disponibile)
                // - Giorni disponibili dopo checkIn: sempre cliccabili come checkout
                const canBeCheckOut = checkIn && !checkOut && day.date > checkIn && (day.available || day.date === firstValidCheckout);
                const isDisabled = day.isPast || (!day.available && !canBeCheckOut);

                return (
                  <button
                    key={i}
                    onClick={() => handleDateClick(day)}
                    onMouseEnter={() => checkIn && !checkOut && setHoverDate(day.date)}
                    onMouseLeave={() => setHoverDate(null)}
                    disabled={isDisabled}
                    className={`
                      aspect-square flex flex-col items-center justify-center text-sm rounded-lg
                      transition-all relative
                      ${isDisabled ? "text-[#D4D4D4] cursor-not-allowed bg-[#FAFAFA]" : "hover:bg-[#FDF9EF] cursor-pointer"}
                      ${inRange && !isSelected ? "bg-[#FDF9EF]" : ""}
                      ${isSelected ? "bg-[#D4AF37] text-white" : ""}
                      ${isCheckInDay ? "rounded-l-lg" : ""}
                      ${isCheckOutDay ? "rounded-r-lg" : ""}
                      ${isFirstValidCheckoutDay ? "ring-2 ring-[#D4AF37] ring-inset" : ""}
                      ${isFirstValidCheckoutDay && !day.available ? "bg-[#FEF3C7]" : ""}
                    `}
                    data-testid={`calendar-day-${day.date}`}
                  >
                    <span className={`font-medium ${isSelected ? "text-white" : ""}`}>
                      {new Date(day.date + "T00:00:00").getDate()}
                    </span>
                    {day.price && !isDisabled && (
                      <span className={`text-[10px] ${isSelected ? "text-white/80" : "text-[#D4AF37]"}`}>
                        €{day.price}
                      </span>
                    )}
                    {!day.available && !day.isPast && !isFirstValidCheckoutDay && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-full h-[1px] bg-[#E7E5E4] rotate-45 absolute" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 pb-4 flex gap-4 text-xs text-[#78716C]">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-white border border-[#E7E5E4]" />
              <span>{t("available")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-[#FAFAFA] relative">
                <span className="w-full h-[1px] bg-[#D4D4D4] rotate-45 absolute top-1/2 left-0" />
              </span>
              <span>{t("unavailable")}</span>
            </div>
          </div>

          {/* Guests selector */}
          <div className="border-t border-[#E7E5E4] p-4">
            <h4 className="font-medium text-sm text-[#1C1917] mb-4 flex items-center gap-2">
              <Users size={16} className="text-[#D4AF37]" />
              {t("maxGuests")}: {maxGuests}
            </h4>
            <div className="grid grid-cols-3 gap-4">
              {/* Adults */}
              <div>
                <label className="text-xs text-[#78716C] uppercase tracking-wider mb-1 block">
                  {t("adults")}
                </label>
                <div className="flex items-center border border-[#E7E5E4] rounded-lg">
                  <button
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    className="px-3 py-2 text-[#78716C] hover:text-[#1C1917]"
                  >−</button>
                  <span className="flex-1 text-center font-medium">{adults}</span>
                  <button
                    onClick={() => !isAtMaxCapacity && setAdults(adults + 1)}
                    className={`px-3 py-2 ${isAtMaxCapacity ? 'text-[#D4D4D4] cursor-not-allowed' : 'text-[#78716C] hover:text-[#1C1917]'}`}
                    disabled={isAtMaxCapacity}
                  >+</button>
                </div>
              </div>

              {/* Children */}
              <div>
                <label className="text-xs text-[#78716C] uppercase tracking-wider mb-1 block">
                  {t("childrenFull")}
                </label>
                <div className="flex items-center border border-[#E7E5E4] rounded-lg">
                  <button
                    onClick={() => setChildren(Math.max(0, children - 1))}
                    className="px-3 py-2 text-[#78716C] hover:text-[#1C1917]"
                  >−</button>
                  <span className="flex-1 text-center font-medium">{children}</span>
                  <button
                    onClick={() => !isAtMaxCapacity && setChildren(children + 1)}
                    className={`px-3 py-2 ${isAtMaxCapacity ? 'text-[#D4D4D4] cursor-not-allowed' : 'text-[#78716C] hover:text-[#1C1917]'}`}
                    disabled={isAtMaxCapacity}
                  >+</button>
                </div>
              </div>

              {/* Children Ages */}
              {children > 0 && (
                <div className="col-span-3 mt-4">
                  <label className="text-xs text-[#78716C] uppercase tracking-wider mb-2 block">
                    {t("selectAge")}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: children }, (_, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm text-[#78716C] min-w-0">
                          {t("childAge")} {index + 1}:
                        </span>
                        <select
                          value={childrenAges[index] || ""}
                          onChange={(e) => handleChildAgeChange(index, e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                        >
                          <option value="">{t("selectAge")}</option>
                          {Array.from({ length: 17 }, (_, age) => age + 1).map(age => (
                            <option key={age} value={age}>
                              {age}Y
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Infants */}
              <div>
                <label className="text-xs text-[#78716C] uppercase tracking-wider mb-1 block flex items-center gap-1">
                  <Baby size={12} /> {t("infantsFull")}
                </label>
                <div className="flex items-center border border-[#E7E5E4] rounded-lg">
                  <button
                    onClick={() => setInfants(Math.max(0, infants - 1))}
                    className="px-3 py-2 text-[#78716C] hover:text-[#1C1917]"
                  >−</button>
                  <span className="flex-1 text-center font-medium">{infants}</span>
                  <button
                    onClick={() => !isAtMaxCapacity && setInfants(infants + 1)}
                    className={`px-3 py-2 ${isAtMaxCapacity ? 'text-[#D4D4D4] cursor-not-allowed' : 'text-[#78716C] hover:text-[#1C1917]'}`}
                    disabled={isAtMaxCapacity}
                  >+</button>
                </div>
              </div>
            </div>
            
            {/* Max capacity message */}
            {isAtMaxCapacity && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700 text-center">
                  {t("maxGuestsReached") || `Hai raggiunto il numero massimo di ${maxGuests} ospiti.`}
                </p>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="px-4 pb-4">
            <div className="bg-[#F9F8F6] rounded-lg p-3 flex items-start gap-2">
              <Info size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#78716C]">
                {t("minNights")}: {minNightsDefault} {t("nights")}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
